import apiClient from '@/lib/api-client';
import type { AuthResponse, ImpersonationState, LoginCredentials, RegisterCredentials, User } from '@/types/auth';

/**
 * Signing in with Google always ends on a session: a first-time signup has its
 * organization provisioned in the callback, and names it on the first
 * onboarding step rather than on a form of its own.
 */
export type GoogleExchangeResult = { status: 'signed_in'; newUser: boolean } & AuthResponse;

class AuthService {
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    const { data } = await apiClient.post<any>('/v1/auth/register', credentials);
    return this.transformAuthResponse(data.data);
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const { data } = await apiClient.post<any>('/v1/auth/login', credentials);
    return this.transformAuthResponse(data.data);
  }

  async getGoogleAuthUrl(payload: {
    flow: 'login' | 'register';
    timezone?: string;
    locale?: string;
    invitationCode?: string;
  }): Promise<string> {
    const { data } = await apiClient.post<any>('/v1/auth/google/authorize', {
      flow: payload.flow,
      timezone: payload.timezone,
      locale: payload.locale,
      invitation_code: payload.invitationCode,
    });
    return data.data.auth_url;
  }

  /** Trades the one-time code for a session. */
  async exchangeGoogleCode(code: string): Promise<GoogleExchangeResult> {
    const { data } = await apiClient.post<any>('/v1/auth/google/exchange', { code });
    const payload = data.data;

    return {
      status: 'signed_in',
      ...this.transformAuthResponse(payload),
      // Authoritative signup flag: it travels with the code's server-side
      // record rather than in the redirect URL.
      newUser: payload?.new_user === true,
    };
  }

  private transformAuthResponse(response: any): AuthResponse {
    // Add organization_id to user if not present but organization is
    if (response.user && response.organization && !response.user.organization_id) {
      response.user.organization_id = response.organization.id;
    }

    return {
      user: this.transformUser(response.user),
      accessToken: response.access_token,
      refreshToken: response.refresh_token
    };
  }

  private transformUser(apiUser: any): User {
    return {
      id: apiUser.id,
      email: apiUser.email,
      firstName: apiUser.first_name,
      lastName: apiUser.last_name,
      phone: apiUser.phone,
      role: apiUser.role,
      organizationId: apiUser.organization_id || 0,
      active: apiUser.active,
      pendingEmail: apiUser.pending_email,
      emailConfirmed: apiUser.email_confirmed || false,
      emailConfirmedAt: apiUser.email_confirmed_at,
      tourCompletedAt: apiUser.tour_completed_at,
      hasProviderProfile: apiUser.has_provider_profile ?? false,
      createdAt: apiUser.created_at,
      updatedAt: apiUser.updated_at
    };
  }

  async logout(): Promise<void> {
    await apiClient.post('/v1/auth/logout');
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const { data } = await apiClient.post<AuthResponse>('/v1/auth/refresh', { refreshToken });
    return data;
  }

  async getCurrentUser(): Promise<User> {
    const { data } = await apiClient.get<{ data: any }>('/v1/auth/me');
    // Backend returns {data: {user: {...}, organization: {...}}, meta: {...}}
    // We need to extract just the user object
    return this.transformUser(data.data.user);
  }

  // Like getCurrentUser but also returns authoritative impersonation state.
  async getSession(): Promise<{ user: User; impersonation: ImpersonationState | null }> {
    const { data } = await apiClient.get<{ data: any }>('/v1/auth/me');
    const imp = data.data.impersonation;
    return {
      user: this.transformUser(data.data.user),
      impersonation: imp
        ? { impersonatorEmail: imp.impersonator_email, sessionId: imp.session_id }
        : null,
    };
  }

  async updateProfile(updates: Partial<User>): Promise<User> {
    const { data } = await apiClient.put<{ data: User }>('/v1/auth/profile', updates);
    return data.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.put('/v1/auth/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }
}

export const authService = new AuthService();
